import {
  useGetStatusColorsQuery,
  useResetStatusColorsMutation,
  useUpdateStatusColorsMutation,
} from 'features/SettingsOld/state/api/settingsApi'
import styles from './configurator.module.scss'
import { useNotification } from 'app/providers'
import { generateBackgroundColor } from './helpers'

import { Col, Modal, Row, Spin } from 'antd'
import { Icon } from '@iconify/react'
import { useFormik } from 'formik'
import { ColorConfiguratorPicker } from './ColorPicker'
import { BUTTON_CONSTANTS } from 'common/constants/buttonConstants'
import { Button } from 'common/components/Button/Button'
import { BUTTON_MODIFIER } from 'common/constants/buttonConstants'
import Show from 'common/components/Show2/Show'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import Popup from 'common/components/Popup'

type Props = {
  open: boolean
  onClose: () => void
}

export const ColorConfigurator = ({ open, onClose }: Props) => {
  const { data, isFetching } = useGetStatusColorsQuery()
  const { setNotification } = useNotification()

  const [updateStatusColors, { isLoading: isUpdating }] = useUpdateStatusColorsMutation()
  const [resetStatusColors, { isLoading: isResetting }] = useResetStatusColorsMutation()

  const { values, dirty, resetForm, setFieldValue, handleSubmit } = useFormik({
    initialValues: {
      statusColors: data,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      const resp = await updateStatusColors(values.statusColors).unwrap()
      if (resp.status) {
        setNotification({
          description: resp.message,
          type: ALERT_CONSTANTS.SUCCESS,
        })
        onClose()
      } else {
        setNotification({
          description: resp.message,
          type: ALERT_CONSTANTS.ERROR,
        })
      }
    },
  })

  const handleColorChange = (color: string, index: number) => {
    const newStatusColors = [...values.statusColors]
    const background = generateBackgroundColor(color)
    newStatusColors[index] = {
      ...newStatusColors[index],
      color,
      background,
    }
    setFieldValue('statusColors', newStatusColors)
  }

  const handleResetColors = () => {
    Popup.showInfo(
      {
        title: 'Are you sure?',
        message: 'You want to reset the colors to Residen default? This action cannot be undone.',
        mainButtonText: 'Yes',
        secondButtonText: 'Cancel',
      },
      () => handleResetStatusColors(),
    )
  }

  const handleResetStatusColors = async () => {
    const resp = await resetStatusColors().unwrap()
    if (resp.status) {
      setNotification({
        description: resp.message,
        type: ALERT_CONSTANTS.SUCCESS,
      })
      onClose()
      resetForm()
    } else {
      setNotification({
        description: resp.message,
        type: ALERT_CONSTANTS.ERROR,
      })
    }
  }

  return (
    <>
      <Modal
        footer={null}
        maskClosable={false}
        open={open}
        onCancel={onClose}
        classNames={{ content: styles.content }}>
        <div className={styles.container}>
          <h3 className={styles.title}>Status Colors</h3>
          <Show>
            <Show.When isVisible={isFetching}>
              <div className={styles.loadingContainer}>
                <Spin />
              </div>
            </Show.When>
            <Show.Else>
              <Spin spinning={isUpdating || isResetting}>
                <form
                  id='color-configurator'
                  onSubmit={handleSubmit}
                  className={styles.colorContainer}>
                  {values.statusColors?.map(({ color, background, name, code }, index) => {
                    return (
                      <ColorConfiguratorPicker
                        key={code}
                        value={color}
                        defaultColor={color}
                        onChange={(newColor) => handleColorChange(newColor, index)}>
                        <div
                          data-updating={isUpdating}
                          onClick={() => setFieldValue('selectedColorIndex', index)}
                          className={styles.colorItem}
                          style={{ background, color }}>
                          <p className={styles.colorItemName}>{name}</p>
                          <Icon icon='ph:pencil-simple' width={24} height={24} />
                        </div>
                      </ColorConfiguratorPicker>
                    )
                  })}
                </form>
              </Spin>
              <Row justify='space-between' gutter={[16, 16]}>
                <Col>
                  <Button
                    disabled={isUpdating || isResetting}
                    modifier={BUTTON_MODIFIER.SECONDARY}
                    onClick={() => {
                      resetForm()
                      onClose()
                    }}>
                    {BUTTON_CONSTANTS.CANCEL}
                  </Button>
                </Col>
                <Col>
                  <Button
                    htmlType='button'
                    disabled={isUpdating || isResetting}
                    modifier={BUTTON_MODIFIER.TERTIARY}
                    onClick={handleResetColors}>
                    Reset
                  </Button>
                </Col>
                <Col>
                  <Button
                    disabled={!dirty || isUpdating || isResetting}
                    htmlType='submit'
                    form='color-configurator'>
                    {BUTTON_CONSTANTS.SUBMIT}
                  </Button>
                </Col>
              </Row>
            </Show.Else>
          </Show>
        </div>
      </Modal>
    </>
  )
}
